import * as React from 'react';
import { LocalizeContextProps, withLocalize } from 'react-localize-redux';
import { connect } from 'react-redux';
import { Privilege, Workspace } from '../../clients/aasServerClient.generated';
import { getAssignablePrivileges } from '../../stores/auth/selectors';
import { RootState } from '../../stores/store';
import FormInput from '../common/FormInput';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { over } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './WorkspacePane.scss';

interface Props extends LocalizeContextProps {
    type: 'edit' | 'new';
    workspace: Partial<Workspace>;
    updateWorkspace: (workspace: Partial<Workspace>) => void;
    saved: boolean;
}

interface State {
    showModal: boolean;
    shouldBeDisabled: boolean;
}

class WorkspaceProperties extends React.Component<Props, State & Partial<Workspace>> {
    overwriteDisabledToEnabled = false;

    public constructor(props: Props) {
        super(props);
        this.state = {
            ...props.workspace,
            showModal: false,
            shouldBeDisabled: false,
        };
        this.props.updateWorkspace(this.state);
        this.overwriteDisabledToEnabled = false;
    }

    public render() {
        const t = this.props.translate;
        const { type, workspace, saved } = this.props;
        const { showModal, sso_private_key, sso_public_key } = this.state;

        const isEditMode = type === 'edit';
        const shouldBeDisabled = !this.overwriteDisabledToEnabled && (isEditMode && !!workspace.sso_private_key && !!workspace.sso_public_key);

        const keysReadOnly = saved && isEditMode && (sso_private_key !== '' && sso_private_key !== null) && (sso_public_key !== '' && sso_public_key !== null);
        const privateKeyDisplay = sso_private_key ? (keysReadOnly ? `${sso_private_key.length <= 10 ? '...' : `${sso_private_key.slice(0, 10)}...`}` : sso_private_key ) : '';

        return (
            <React.Fragment>
                <FormInput
                    row={true}
                    name="id"
                    fullName={t('workspaces.edit.field.id') as string}
                    readOnly={true}
                    defaultValue={this.state.id}
                />
                <FormInput
                    row={true}
                    name="name"
                    fullName={t('workspaces.edit.field.name') as string}
                    value={this.state.name || ''}
                    onChange={this.handleWorkspaceChange}
                />
                <FormInput
                    row={true}
                    name="host"
                    fullName={t('workspaces.edit.field.host') as string}
                    value={this.state.host || ''}
                    onChange={this.handleWorkspaceChange}
                />
                <FormInput
                    row={true}
                    name="accent_color"
                    fullName={t('workspaces.edit.field.accentColor') as string}
                    value={this.state.accent_color || ''}
                    onChange={this.handleWorkspaceChange}
                />
                <FormInput
                    row={true}
                    name="user_limit"
                    fullName={t('workspaces.edit.field.userLimit') as string}
                    value={this.state.user_limit || ''}
                    onChange={this.handleWorkspaceChange}
                />
                <FormInput
                    row={true}
                    name="background_img"
                    fullName={t('workspaces.edit.field.backgroundImage') as string}
                    value={this.state.background_img || ''}
                    onChange={this.handleWorkspaceChange}
                />
                <FormInput
                    row={true}
                    name="logo_img"
                    fullName={t('workspaces.edit.field.logoImage') as string}
                    value={this.state.logo_img || ''}
                    onChange={this.handleWorkspaceChange}
                />
                <FormInput
                    row={true}
                    name="icon_img"
                    fullName={t('workspaces.edit.field.iconImage') as string}
                    value={this.state.icon_img || ''}
                    onChange={this.handleWorkspaceChange}
                />
                <FormInput
                    row={true}
                    name="org_name"
                    fullName={t('workspaces.edit.field.organizationName') as string}
                    value={this.state.org_name || ''}
                    onChange={this.handleWorkspaceChange}
                />
                <FormInput
                    row={true}
                    name="favicon"
                    fullName={t('workspaces.edit.field.favicon') as string}
                    value={this.state.favicon || ''}
                    onChange={this.handleWorkspaceChange}
                />
                <FormInput
                    row={true}
                    name="ip_restricted"
                    type="checkbox"
                    fullName={t('workspaces.edit.field.ipRestricted') as string}
                    checked={this.state.ip_restricted ?? false}
                    checkboxProp={true}
                    onChange={this.handleWorkspaceChange}
                />
                <FormInput
                    row={true}
                    name="sso_callback"
                    type="text"
                    fullName={t('workspaces.edit.field.ssoCallback') as string}
                    value={this.state.sso_callback ?? ''}
                    onChange={this.handleWorkspaceChange}
                />
                <FormInput
                    row={true}
                    name="sso_entrypoint"
                    type="text"
                    fullName={t('workspaces.edit.field.ssoEntrypoint') as string}
                    value={this.state.sso_entrypoint ?? ''}
                    onChange={this.handleWorkspaceChange}
                />
                <FormInput
                    row={true}
                    name="sso_issuer"
                    type="text"
                    fullName={t('workspaces.edit.field.ssoIssuer') as string}
                    value={this.state.sso_issuer ?? ''}
                    onChange={this.handleWorkspaceChange}
                />
                <FormInput
                    row={true}
                    name="sso_logout"
                    type="text"
                    fullName={t('workspaces.edit.field.ssoLogout') as string}
                    value={this.state.sso_logout ?? ''}
                    onChange={this.handleWorkspaceChange}
                />
                <FormInput
                    row={true}
                    name="sso_logoutcallback"
                    type="text"
                    fullName={t('workspaces.edit.field.ssoLogoutCallback') as string}
                    value={this.state.sso_logoutcallback ?? ''}
                    onChange={this.handleWorkspaceChange}
                />
                <FormInput
                    row={true}
                    name="sso_certificate"
                    type="text"
                    fullName={t('workspaces.edit.field.ssoCertificate') as string}
                    value={this.state.sso_certificate ?? ''}
                    onChange={this.handleWorkspaceChange}
                />
                <div className="key-container">
                <div className={shouldBeDisabled?"key-inputs col-sm-11 p-0":"key-inputs col-sm-12 p-0"}>
                <FormInput
                    row={true}
                    name="sso_private_key"
                    type="text"
                    fullName={t('workspaces.edit.field.ssoPrivateKey') as string}
                    value={isEditMode ? privateKeyDisplay : (sso_private_key || '')}
                    onChange={this.handleWorkspaceChange}
                    disabled={shouldBeDisabled} 
                />
                <FormInput
                    row={true}
                    name="sso_public_key"
                    type="text"
                    fullName={t('workspaces.edit.field.ssoPublicKey') as string}
                    value={isEditMode ? sso_public_key : (sso_public_key || '')}
                    onChange={this.handleWorkspaceChange}
                    disabled={shouldBeDisabled} 
                />
              
                </div>
                {shouldBeDisabled && (
                  <div className='col-sm-1 py-0 my-0'>  <Button color="danger" onClick={this.toggleModal} className="delete-button w-100 ml-3">
                    <FontAwesomeIcon icon={['fal', 'trash']} className="noMarginRight" />
                    </Button>
                    </div>
                )}
                </div>
                <Modal isOpen={showModal} toggle={this.toggleModal}>
                    <ModalHeader toggle={this.toggleModal}>
                        {t('workspaces.edit.modal.header')}
                    </ModalHeader>
                    <ModalBody>
                        {t('workspaces.edit.modal.body')}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="danger" onClick={this.deleteKeys}>
                            {t('workspaces.edit.modal.confirm')}
                        </Button>
                        <Button color="secondary" onClick={this.toggleModal}>
                            {t('workspaces.edit.modal.cancel')}
                        </Button>
                    </ModalFooter>
                </Modal>
                <FormInput
                    row={true}
                    name='mail_template'
                    type='text'
                    fullName={t('workspaces.edit.field.mailTemplate') as string}
                    value={this.state.mail_template ?? ''}
                    onChange={this.handleWorkspaceChange}
                />
                <FormInput
                    row={true}
                    name='etl_url'
                    type='url'
                    pattern='https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)'
                    fullName={t('workspaces.edit.field.etlUrl') as string}
                    value={this.state.etl_url ?? ''}
                    onChange={this.handleWorkspaceChange}
                    required
                />
            </React.Fragment>
        );
    }

    private handleWorkspaceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const update: Partial<State> = {
            [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
        };
        this.setState((prevState) => ({
            ...prevState,
            ...update
        }));

        this.props.updateWorkspace({ ...this.state, ...update });
        
    }

    private toggleModal = () => {
        this.setState({ showModal: !this.state.showModal });
    }

    private deleteKeys = () => {
        this.setState({
            sso_private_key: '',
            sso_public_key: '',
            showModal: false,
            shouldBeDisabled: false,
        });
        this.props.updateWorkspace({ ...this.state, sso_private_key: '', sso_public_key: '' });
        this.overwriteDisabledToEnabled = true;
    }
}

const mapStateToProps = (state: RootState) => ({
    assignable: getAssignablePrivileges(state),
});

export default connect(mapStateToProps)(withLocalize(WorkspaceProperties));